/**
 * Cliente HTTP del historial de sesiones para `apps/speak`.
 *
 * Pega contra el módulo english del backend
 * (`apps/backend/src/routes/english/sessions.ts`):
 *   - `GET  /sessions?limit&cursor` → lista paginada por cursor (DESC por fecha).
 *     El cursor es OPACO: se re-manda tal cual vino, nunca se construye acá
 *     (el backend lo emite con la precisión de Postgres — ver pg-cursor.ts).
 *   - `DELETE /sessions/:id`        → soft-delete (oculta del historial,
 *                                     preserva auditoría de billing).
 *
 * El detalle de una sesión NO vive acá: reusa `fetchSessionSummary` /
 * `useSessionSummary` (mismo endpoint `/sessions/:id/summary` que el debrief).
 *
 * Tipos redefinidos localmente (no se importan de `apps/client`) para que
 * speak quede autocontenida — misma decisión que `sessionSummaryApi.ts`.
 */
import { createTypedApiClient } from '@orborix/auth-client';

import type { CefrLevel } from './sessionSummaryApi.js';
import type {
  SessionOrigin,
  TranscriptGameRound,
  TranscriptGameSolution,
} from '@orborix/shared-types';

const BASE = '/api/modules/english';

/**
 * Tipo de actividad. Hoy el backend sólo persiste conversaciones de voz; los
 * otros valores existen para el filtro del historial y se poblarán cuando
 * lecciones/drills/repasos sean sesiones reales.
 */
export type SessionActivityType = 'conversation' | 'lesson' | 'drill' | 'review';
export type SessionMode = 'plan' | 'free' | 'calibration';

export interface SessionHistoryItem {
  sessionId: string;
  type: SessionActivityType;
  title: string;
  mode: SessionMode;
  intensity: string;
  unitId: string | null;
  scenarioId: string | null;
  /**
   * What a free conversation was about when the learner brought it from the
   * Audioteca, a news article or Órbitas (`voice_sessions.metadata.origin`).
   * Null = plain free talk, a role-play, the Program, or a session older than
   * the field.
   */
  origin: SessionOrigin | null;
  cefrEstimate: CefrLevel | null;
  /**
   * Wall-clock en segundos (started→ended). Null si la sesión no cerró.
   * **Ninguna superficie lo muestra**: es dato crudo de la sesión (auditoría /
   * debug), no tiempo de práctica — ver `activeSeconds`.
   */
  durationSeconds: number | null;
  /**
   * Tiempo ACTIVO de la charla en segundos (`voice_sessions.active_seconds`).
   * Es la señal de "tiempo de práctica" en TODA la app —minutos de la Home,
   * heatmap, fila y detalle del historial, medalla del debrief, sub del
   * movimiento en la billetera— y la misma que `/progress/daily` agrega por
   * día. Las dos alternativas miden mal: `durationSeconds` es wall-clock y se
   * dispara a horas si la sesión quedó abierta, y el audio intercambiado
   * (STT+TTS) daba 0 en una charla escrita.
   * Null = sin fila de sesión de voz (historial viejo).
   */
  activeSeconds: number | null;
  /** Turnos persistidos (para "X mensajes"). */
  turnCount: number;
  /** Palabras nuevas extraídas (memory facts kind=vocab). */
  vocabCount: number;
  correctionsCount: number;
  /** ISO timestamp de creación de la métrica de sesión (≈ cierre). */
  createdAt: string;
  totalCostNano: string;
  totalCostUsd: string;
  /** Si esta sesión fue retomada, apunta a la anterior del hilo. Null si arrancó de cero. */
  parentSessionId?: string | null;
}

export interface SessionHistoryPage {
  items: SessionHistoryItem[];
  /** Cursor OPACO para la próxima página. Null = no hay más. */
  nextCursor: string | null;
}

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'SessionsApiError',
  // El historial cancela en mid-flight al desmontar: el caller distingue
  // "aborté yo" de "se cayó la red".
  abortError: 'rethrow',
});

export const SessionsApiError = client.ApiError;
export type SessionsApiError = InstanceType<typeof client.ApiError>;

export interface ListSessionsParams {
  limit?: number;
  /** Cursor opaco del backend: devuelve sesiones estrictamente más viejas. */
  cursor?: string | null;
  /** Incluir sesiones "vacías" (sin turnos pero reales). Default false. */
  includeEmpty?: boolean;
}

/**
 * Lista una página del historial. `signal` permite cancelar en mid-flight
 * (el caller aborta al desmontar para no setState tras unmount).
 */
export async function listSessions(
  params: ListSessionsParams = {},
  signal?: AbortSignal,
): Promise<SessionHistoryPage> {
  const qs = new URLSearchParams();
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.includeEmpty) qs.set('includeEmpty', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';

  return client.get<SessionHistoryPage>(`/sessions${suffix}`, signal ? { signal } : {});
}

/**
 * Techo de sesiones que se traen para el detalle de UN día.
 *
 * El modo día del endpoint NO pagina, así que este número es un tope duro: un
 * día con más de {@link DAY_SESSIONS_LIMIT} sesiones mostraría sólo las más
 * recientes y su desglose sub-sumaría el total del día. Se acepta porque no es
 * alcanzable en la práctica (serían ~50 charlas con turnos en 24 h) y el total
 * NO depende de esto — sale de `/progress/daily`. Se pasa siempre explícito:
 * omitirlo dejaría el default de 20 del endpoint, que sí es alcanzable.
 */
const DAY_SESSIONS_LIMIT = 50;

/**
 * Sesiones de un día calendario, para el detalle de la hoja de racha. Se carga
 * lazy al abrir el día: la Home ya no trae el historial entero sólo para poder
 * desglosar un día que quizá nadie abra.
 *
 * El día lo resuelve el BACKEND (`?day=&tz=`) sobre `voice_sessions.started_at`
 * con el mismo filtro que `/progress/daily`. Acotarlo acá por cursor sería
 * incorrecto: el `createdAt` que pagina el listado es ≈ el CIERRE de la sesión,
 * así que una charla que cruza la medianoche caería en un día distinto del que
 * la contó en el total.
 */
export async function listSessionsForDay(
  /** Etiqueta `YYYY-MM-DD` del día, en `tz`. */
  day: string,
  /** Zona horaria IANA — la misma con la que se pidió `/progress/daily`. */
  tz: string,
  signal?: AbortSignal,
): Promise<SessionHistoryItem[]> {
  const qs = new URLSearchParams({
    limit: String(DAY_SESSIONS_LIMIT),
    day,
    tz,
  });

  const page = await client.get<SessionHistoryPage>(
    `/sessions?${qs.toString()}`,
    signal ? { signal } : {},
  );
  return page.items;
}

/**
 * Soft-delete de una sesión. Idempotente: un 404 (ya borrada / no existe) se
 * trata como éxito, porque el estado deseado — que no esté en el historial —
 * ya se cumple. Sólo lanza ante errores reales (5xx, red).
 */
export async function deleteSession(sessionId: string): Promise<void> {
  const res = await client.request(`/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
  });
  if (res.status === 404) return;
  await client.ensureOk(res);
}

/**
 * Marca/desmarca una sesión EN CURSO como "charla privada" (incógnito). El
 * backend al cerrar relee este flag → salta memoria + analyze, y la oculta del
 * historial. Best-effort desde la UI: si falla, no rompemos la conversación.
 */
export async function setSessionIncognito(sessionId: string, incognito: boolean): Promise<void> {
  // `ensureOk` en vez de `send`: este endpoint nunca leyó su body de éxito.
  const res = await client.request(`/sessions/${encodeURIComponent(sessionId)}/incognito`, {
    method: 'POST',
    body: JSON.stringify({ incognito }),
  });
  await client.ensureOk(res);
}

export interface TranscriptGrammarCorrection {
  kind: 'grammar';
  id: string;
  said: string;
  fix: string;
  explanation: string | null;
  category: string | null;
  severity: string;
  /** Voto previo del usuario (plan 26 §7.4) — rehidrata los thumbs. */
  feedback?: { vote: 'up' | 'down'; reason?: string };
}
export interface TranscriptPhoneticCorrection {
  kind: 'phonetic';
  id: string;
  word: string;
  saidIpa: string | null;
  targetIpa: string | null;
  tip: string | null;
}
export type TranscriptCorrection = TranscriptGrammarCorrection | TranscriptPhoneticCorrection;

export type TurnAudioKind = 'user' | 'assistant';

export interface TranscriptTurn {
  turnIndex: number;
  userText: string | null;
  assistantText: string | null;
  /** Correcciones (gramática + fonética) atadas a este turno. */
  corrections: TranscriptCorrection[];
  /** Hay audio real persistido del usuario para este turno (RECORD_SESSION_AUDIO). */
  hasUserAudio: boolean;
  /** Hay audio real persistido del coach para este turno. */
  hasAssistantAudio: boolean;
  /**
   * The turn's corrections verdict as it was sent live (`corrections.done`,
   * persisted since migration 0393). Only `ok` means the corrections above
   * are the complete answer; `null` = unknown (a turn before the table, a
   * learner-cancelled turn, a lost write) — never a clean sentence.
   */
  correctionsVerdict: 'ok' | 'failed' | 'skipped' | null;
  /**
   * The translate-it round this turn answered (plan 47, table
   * `language_game_rounds`): the challenge it tried and the persisted meaning
   * verdict, and the sentence the check judged. `null` = not a game attempt.
   * The stamp is `gameTurnStampOf` on them + `userText`, `corrections` and
   * `correctionsVerdict` — the same call as live (`selectTranscriptGameStamps`).
   */
  gameRound: TranscriptGameRound | null;
}

/** Transcripción de una sesión + links del hilo (resume). */
export interface SessionTranscriptResult {
  turns: TranscriptTurn[];
  /** Sesión de la que ésta es continuación. Null si arrancó de cero. */
  parentSessionId: string | null;
  /** Sesiones que retomaron ésta. Vacío si no hay. */
  childSessionIds: string[];
  /**
   * The «No sé» / «Ver solución» answers of the session (plan 47 Round 2,
   * table `language_game_solutions`), in request order. `[]` from a server
   * older than the field.
   */
  gameSolutions: TranscriptGameSolution[];
}

/** Transcripción (turnos en orden cronológico) de una sesión + links del hilo. */
export async function fetchSessionTranscript(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionTranscriptResult> {
  const data = await client.get<{
    turns: TranscriptTurn[];
    parentSessionId?: string | null;
    childSessionIds?: string[];
    gameSolutions?: TranscriptGameSolution[];
  }>(`/sessions/${encodeURIComponent(sessionId)}/transcript`, signal ? { signal } : {});
  return {
    turns: data.turns,
    parentSessionId: data.parentSessionId ?? null,
    childSessionIds: data.childSessionIds ?? [],
    // Absent = a server older than Round 2: no solutions to show.
    gameSolutions: data.gameSolutions ?? [],
  };
}

const AUDIO_RETRY_DELAY_MS = 400;

/**
 * Trae el audio REAL persistido de un turno (WAV grabado en la sesión) como
 * Blob, para reproducirlo con `useAudioBlob`. No re-sintetiza TTS.
 *
 * `retries`: reintentos ante 404 (el archivo se persiste async al cerrar el
 * turno). El historial no lo necesita (ya está persistido); la conversación en
 * vivo sí, porque el play puede aparecer un instante antes de que el WAV se
 * escriba. Default 0.
 */
export async function fetchTurnAudioBlob(
  sessionId: string,
  turnIndex: number,
  kind: TurnAudioKind,
  opts: { retries?: number } = {},
): Promise<Blob> {
  const path = `/sessions/${encodeURIComponent(sessionId)}/turns/${turnIndex}/audio?kind=${kind}`;
  const maxRetries = opts.retries ?? 0;
  for (let attempt = 0; ; attempt++) {
    const res = await client.request(path);
    if (res.ok) return res.blob();
    // 404 = el WAV todavía no está (race del cierre de turno) → reintentar.
    if (res.status === 404 && attempt < maxRetries) {
      await new Promise((r) => setTimeout(r, AUDIO_RETRY_DELAY_MS));
      continue;
    }
    await client.ensureOk(res);
  }
}

/**
 * Motivo legible de un fallo de `fetchTurnAudioBlob`, para el tooltip del botón
 * de escuchar. `useAudioBlob` se traga el error (sólo deja `state: 'error'`):
 * sin esto, un 404 y un fallo de reproducción se pintan idénticos y no se
 * puede diagnosticar nada. Compartido por la burbuja del user y el debrief.
 */
export function describeTurnAudioError(err: unknown): string {
  const status = (err as { status?: number } | null)?.status;
  if (status === 404) return 'el backend no tiene el audio de este turno';
  if (status === 401 || status === 403) return 'la sesión expiró';
  if (status === 0) return 'no se pudo conectar';
  if (typeof status === 'number') return `error ${status}`;
  return 'no se pudo reproducir';
}
